import {
  TapUser,
  TapStatus,
  StartSessionResponse,
  TapsRequest,
  TapsResponse,
  EndSessionResponse,
  LeaderboardEntry,
} from '@/domain/types';
import { API_BASE_URL } from '@/domain/constants';

/**
 * API client for tap game endpoints.
 * All requests use the tma auth header from Telegram initData.
 */
class TapApiClient {
  private initData: string = '';

  setInitData(initData: string) {
    console.log('[tapApi] setInitData called, length:', initData?.length);
    this.initData = initData;
  }

  getInitData() {
    return this.initData;
  }

  private async fetch<T>(
    path: string,
    options: (RequestInit & { timeoutMs?: number }) = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${path}`;
    const { timeoutMs = 10_000, ...requestInit } = options;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true', // Skip ngrok interstitial page
      ...requestInit.headers,
    };

    if (this.initData) {
      (headers as Record<string, string>)['Authorization'] = `tma ${this.initData}`;
    }

    const timeoutController = new AbortController();
    let timeoutId: number | undefined;

    const signal = (() => {
      const providedSignal = requestInit.signal;
      if (!providedSignal) return timeoutController.signal;

      if (typeof AbortSignal !== 'undefined' && 'any' in AbortSignal) {
        return AbortSignal.any([providedSignal, timeoutController.signal]);
      }

      if (providedSignal.aborted) {
        timeoutController.abort();
      } else {
        providedSignal.addEventListener('abort', () => timeoutController.abort(), { once: true });
      }
      return timeoutController.signal;
    })();

    const parseJsonIfPresent = async (response: Response): Promise<unknown | null> => {
      if (response.status === 204) return null;

      const contentType = response.headers.get('content-type') ?? '';
      const raw = await response.text().catch(() => '');
      if (!raw) return null;

      if (contentType.includes('application/json')) {
        try {
          return JSON.parse(raw);
        } catch {
          return raw;
        }
      }

      // Non-JSON response body (e.g. HTML error page from proxy)
      return raw;
    };

    try {
      timeoutId = window.setTimeout(() => {
        timeoutController.abort();
      }, timeoutMs);

      const response = await fetch(url, {
        ...requestInit,
        headers,
        signal,
      });

      if (!response.ok) {
        const errorBody = await parseJsonIfPresent(response);
        const message = extractErrorMessage(errorBody) ?? `HTTP ${response.status} ${response.statusText}`;
        throw new TapApiError(response.status, message, {
          url,
          statusText: response.statusText,
          details: errorBody,
        });
      }

      const body = await parseJsonIfPresent(response);
      return body as T;
    } catch (err) {
      if (err instanceof TapApiError) throw err;

      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new TapApiError(0, 'Request timed out', { url });
      }

      throw new TapApiError(0, err instanceof Error ? err.message : 'Network error', { url });
    } finally {
      if (timeoutId) window.clearTimeout(timeoutId);
    }
  }

  // GET /tap/me - Get current user
  async getMe(): Promise<TapUser> {
    return this.fetch<TapUser>('/tap/me');
  }

  // GET /tap/status - Get tap status
  async getStatus(): Promise<TapStatus> {
    return this.fetch<TapStatus>('/tap/status');
  }

  // POST /tap/session/start - Start new session
  async startSession(): Promise<StartSessionResponse> {
    return this.fetch<StartSessionResponse>('/tap/session/start', {
      method: 'POST',
    });
  }

  // POST /tap/session/taps - Submit tap batch
  async submitTaps(request: TapsRequest): Promise<TapsResponse> {
    return this.fetch<TapsResponse>('/tap/session/taps', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // POST /tap/session/end - End session
  async endSession(sessionId: string): Promise<EndSessionResponse> {
    return this.fetch<EndSessionResponse>('/tap/session/end', {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    });
  }

  // GET /leaderboard - Get leaderboard (reuse existing endpoint)
  async getLeaderboard(): Promise<{ entries: LeaderboardEntry[]; aroundMe: LeaderboardEntry[]; userRank: number | null }> {
    const data = await this.fetch<{ leaderboard: LeaderboardEntry[]; aroundMe: LeaderboardEntry[]; userRank: number | null }>('/leaderboard');
    return {
      entries: data.leaderboard ?? [],
      aroundMe: data.aroundMe ?? [],
      userRank: data.userRank ?? null,
    };
  }

  // POST /tap/wallet-link-token - Create token for wallet linking on promo-site
  async createWalletLinkToken(): Promise<{ token: string; url: string }> {
    return this.fetch<{ token: string; url: string }>('/tap/wallet-link-token', {
      method: 'POST',
    });
  }
}

export class TapApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public info?: {
      url?: string;
      statusText?: string;
      details?: unknown;
    }
  ) {
    super(message);
    this.name = 'TapApiError';
  }
}

function extractErrorMessage(errorBody: unknown): string | null {
  if (!errorBody) return null;
  if (typeof errorBody === 'string') return errorBody;

  if (typeof errorBody === 'object') {
    const maybeDetail = (errorBody as { detail?: unknown }).detail;
    if (typeof maybeDetail === 'string') return maybeDetail;
    if (typeof maybeDetail === 'object' && maybeDetail) {
      const detailObj = maybeDetail as { message?: unknown; code?: unknown };
      if (typeof detailObj.message === 'string') return detailObj.message;
      if (typeof detailObj.code === 'string') return detailObj.code;
    }

    const maybeMessage = (errorBody as { message?: unknown }).message;
    if (typeof maybeMessage === 'string') return maybeMessage;
  }

  return null;
}

// Singleton instance
export const tapApi = new TapApiClient();
